'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { CalendarX, CalendarCheck, ArrowRight, Star, Users, Anchor, Loader2, Ship } from 'lucide-react';
import { Modal, MODAL_LAYERS } from '@/components/ui/Modal';
import { Boat } from '@/types';
import { findNearestAvailable, DateSuggestion } from '@/services/bookingAvailabilityService';

interface UnavailableBoatModalProps {
  isOpen: boolean;
  onClose: () => void;
  boat: Boat;
  startDate: string;
  endDate: string;
  similarBoats: Boat[];
  onSelectDates?: (startDate: string, endDate: string) => void;
}

const formatDate = (dateStr: string) => {
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const UnavailableBoatModal: React.FC<UnavailableBoatModalProps> = ({
  isOpen,
  onClose,
  boat,
  startDate,
  endDate,
  similarBoats,
  onSelectDates,
}) => {
  const [suggestions, setSuggestions] = useState<DateSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  // Distingue motivo non-prenotabile: sospensione vendor/boat → nessuna suggestion
  // date alternative (sarebbero sospese ugualmente); range-blocked = override
  // NOT_AVAILABLE o booking overlap → suggerisci date alternative.
  const [suspensionReason, setSuspensionReason] = useState<'global' | 'vendor' | 'boat' | null>(null);

  useEffect(() => {
    if (!isOpen || !boat?.id) return;
    const API = process.env.NEXT_PUBLIC_API_URL || '/api';
    fetch(`${API}/settings/public/bookings-status?boatId=${boat.id}`)
      .then(r => r.json())
      .then(d => {
        if (d.data?.suspended) setSuspensionReason('global');
        else if (d.data?.vendorSuspended) setSuspensionReason('vendor');
        else if (d.data?.boatSuspended) setSuspensionReason('boat');
        else setSuspensionReason(null);
      })
      .catch(() => setSuspensionReason(null));
  }, [isOpen, boat?.id]);

  useEffect(() => {
    if (!isOpen || !boat?.id || !startDate || !endDate) return;
    if (suspensionReason) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    findNearestAvailable(boat.id, startDate, endDate)
      .then(data => {
        setSuggestions(data.suggestions || []);
      })
      .catch(() => {
        setSuggestions([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, boat?.id, startDate, endDate, suspensionReason]);

  const subtitleText = suspensionReason === 'global'
    ? `Prenotazioni online temporaneamente sospese su tutta la piattaforma.`
    : suspensionReason === 'vendor'
    ? `Il proprietario di ${boat.name} ha sospeso temporaneamente tutte le prenotazioni.`
    : suspensionReason === 'boat'
    ? `${boat.name} ha le prenotazioni sospese temporaneamente dal proprietario.`
    : `${boat.name} non è disponibile dal ${formatDate(startDate)} al ${formatDate(endDate)}`;

  const titleText = suspensionReason ? 'Barca temporaneamente non prenotabile' : 'Date non disponibili';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={titleText}
      subtitle={subtitleText}
      icon={<CalendarX className="w-5 h-5 text-amber-600" />}
      size="sm"
      zIndex={MODAL_LAYERS.picker - 1000}
    >
      <div className="space-y-6" data-cid="catalog-unavailable-modal-body">
        {/* Suggested Alternative Dates: nascoste se sospensione attiva (date
            diverse non aiutano: barca resterebbe sospesa). */}
        {!suspensionReason && (
        <div data-cid="catalog-unavailable-suggestions">
          <div className="flex items-center gap-2 mb-3">
            <CalendarCheck className="w-5 h-5 text-emerald-500" />
            <h4 className="font-bold text-slate-900">Date alternative disponibili</h4>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-slate-500">Ricerca date disponibili...</span>
            </div>
          ) : suggestions.length > 0 ? (
            <div className="space-y-2">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    if (onSelectDates) {
                      onSelectDates(suggestion.startDate, suggestion.endDate);
                    }
                    onClose();
                  }}
                  className="w-full flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                      <CalendarCheck className="w-4 h-4 text-emerald-500" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-semibold text-slate-900">
                        {formatDate(suggestion.startDate)} — {formatDate(suggestion.endDate)}
                      </p>
                      <p className="text-xs text-slate-500">
                        {suggestion.offsetDays} {suggestion.offsetDays === 1 ? 'giorno' : 'giorni'}{' '}
                        {suggestion.direction === 'after' ? 'dopo' : 'prima'}
                      </p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors" />
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 py-3">
              Nessuna data disponibile nelle prossime settimane per questa barca.
            </p>
          )}
        </div>
        )}

        {/* Similar Boats */}
        {similarBoats.length > 0 && (
          <div data-cid="catalog-unavailable-similar">
            <div className="flex items-center gap-2 mb-3">
              <Ship className="w-5 h-5 text-blue-500" />
              <h4 className="font-bold text-slate-900">Barche simili disponibili</h4>
            </div>

            <div className="space-y-2">
              {similarBoats.slice(0, 4).map(similar => (
                <Link
                  key={similar.id}
                  href={`/boat/${similar.slug ?? similar.id}?startDate=${startDate}&endDate=${endDate}`}
                  onClick={onClose}
                  className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
                >
                  <img
                    src={similar.image}
                    alt={similar.name}
                    className="w-14 h-14 rounded-xl object-cover shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] font-black text-blue-600 uppercase tracking-wider">{similar.type}</p>
                    <p className="text-sm font-bold text-slate-900 truncate">{similar.name}</p>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Anchor className="w-3 h-3" />
                        {similar.location}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-slate-500">
                        <Users className="w-3 h-3" />
                        {similar.capacity}
                      </span>
                      {similar.rating > 0 && (
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
                          {similar.rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-lg font-bold text-slate-900">&euro;{similar.price}</p>
                    <p className="text-[10px] text-slate-400">/giorno</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* View boat detail anyway */}
        <div className="pt-2 border-t border-slate-100" data-cid="catalog-unavailable-view-anyway">
          <Link
            href={`/boat/${boat.slug ?? boat.id}`}
            onClick={onClose}
            className="block w-full text-center py-3 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"
          >
            Visualizza comunque {boat.name} &rarr;
          </Link>
        </div>
      </div>
    </Modal>
  );
};

export default UnavailableBoatModal;
